import React, { useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text, Billboard } from '@react-three/drei';
import { Shape } from 'three';
import * as THREE from 'three';

interface FloatingNameTagWithEventsProps {
  name: string;
  position: [number, number, number];
  /** Height of the tag above the avatar's feet (default 1.92 m, human). */
  height?: number;
}

const FONT = '/fonts/outfit-600.woff';
const FONT_SIZE = 0.062;
const PAD_X = 0.045;
const HEIGHT = 0.095;
const MAX_CHARS = 22;
// Grow with distance (clamped) so far names stay readable and near ones stay small.
const NEAR = 3.5;
const MAX_SCALE = 2.4;
const _p = new THREE.Vector3();

// Pill with rounded ends, centred on the origin.
function createPillShape(width: number, height: number): Shape {
  const shape = new Shape();
  const r = height / 2;
  const hw = Math.max(width / 2, r);
  shape.moveTo(-hw + r, r);
  shape.lineTo(hw - r, r);
  shape.absarc(hw - r, 0, r, Math.PI / 2, -Math.PI / 2, true);
  shape.lineTo(-hw + r, -r);
  shape.absarc(-hw + r, 0, r, -Math.PI / 2, Math.PI / 2, true);
  shape.closePath();
  return shape;
}

// Name above an avatar's head. The pill is sized to the rendered text, so
// padding is even for every name. (The old mic dot listened to the local
// user's mute events, so every tag showed *your* mute state — removed.)
const FloatingNameTagWithEvents: React.FC<FloatingNameTagWithEventsProps> = ({ name, height = 1.92 }) => {
  const label = name.length > MAX_CHARS ? `${name.slice(0, MAX_CHARS - 1)}…` : name;
  // Estimate until troika reports the real width.
  const [textWidth, setTextWidth] = useState(() => label.length * FONT_SIZE * 0.55);
  const width = textWidth + PAD_X * 2;

  const bgShape = useMemo(() => createPillShape(width, HEIGHT), [width]);
  const edgeShape = useMemo(() => createPillShape(width + 0.008, HEIGHT + 0.008), [width]);

  const scaler = useRef<THREE.Group>(null);
  useFrame(({ camera }) => {
    const g = scaler.current;
    if (!g) return;
    const d = g.getWorldPosition(_p).distanceTo(camera.position);
    g.scale.setScalar(Math.min(MAX_SCALE, Math.max(1, d / NEAR)));
  });

  return (
    <Billboard position={[0, height, 0]}>
      <group ref={scaler}>
      <mesh position={[0, 0, -0.002]} renderOrder={10}>
        <shapeGeometry args={[edgeShape]} />
        <meshBasicMaterial color="#5ee6dc" transparent opacity={0.35} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh renderOrder={11}>
        <shapeGeometry args={[bgShape]} />
        <meshBasicMaterial color="#0a1520" transparent opacity={0.62} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
      <Text
        font={FONT}
        color="#f2fbfc"
        fontSize={FONT_SIZE}
        letterSpacing={0.02}
        anchorX="center"
        anchorY="middle"
        position={[0, 0.002, 0.002]}
        renderOrder={12}
        onSync={(t: any) => {
          const b = t.textRenderInfo?.blockBounds;
          if (b) {
            const w = b[2] - b[0];
            if (Math.abs(w - textWidth) > 0.001) setTextWidth(w);
          }
        }}
      >
        {label}
      </Text>
      </group>
    </Billboard>
  );
};

export default FloatingNameTagWithEvents;
